import React, { useState } from 'react';
import { Stack, CircularProgress, Alert, TwoPaneLayout, Tabs } from '@metarouter-private/mr-mui';
import { MrAppMain } from '@ion/components';
import { useQueryWebhookConfig, useTrackPage, isAllowedToWriteWebhookConfig } from '@ion/api';
import Documentation from './components/Documentation';
import EmptyState from './components/EmptyState';
import CreateWebhookForm from './components/CreateWebhookForm';
import WebhookConfigDisplay from './components/WebhookConfigDisplay';
import SecretDisplayModal from './components/SecretDisplayModal';
import DeliveryLogsTab from './components/DeliveryLogs';

export default function WebhookSettingsPage() {
  const { webhookConfig, loading, error, isAllowed } = useQueryWebhookConfig();
  const canWrite = isAllowedToWriteWebhookConfig();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [hmacSecret, setHmacSecret] = useState(null);

  useTrackPage('webhook_settings_page_viewed');

  if (loading) {
    return (
      <MrAppMain styleNames="noBackground">
        <CircularProgress />
      </MrAppMain>
    );
  }

  if (!isAllowed) {
    return (
      <MrAppMain styleNames="noBackground">
        <Alert severity="warning">You do not have permission to view webhook configuration.</Alert>
      </MrAppMain>
    );
  }

  if (error) {
    return (
      <MrAppMain styleNames="noBackground">
        <Alert severity="error">Failed to load webhook configuration.</Alert>
      </MrAppMain>
    );
  }

  return (
    <MrAppMain styleNames="noBackground">
      <TwoPaneLayout
        paneOne={<Documentation />}
        paneTwo={
          <Stack spacing={3}>
            {!webhookConfig && !showCreateForm && (
              <EmptyState canWrite={canWrite} onCreateClick={() => setShowCreateForm(true)} />
            )}
            {canWrite && showCreateForm && !webhookConfig && (
              <CreateWebhookForm
                onCancel={() => setShowCreateForm(false)}
                onCreated={secret => setHmacSecret(secret)}
              />
            )}
            {webhookConfig && (
              <Tabs
                tabs={[
                  {
                    label: 'Configuration',
                    content: <WebhookConfigDisplay canWrite={canWrite} webhookConfig={webhookConfig} />,
                  },
                  {
                    label: 'Delivery Logs',
                    content: <DeliveryLogsTab webhookConfigId={webhookConfig.id} />,
                  },
                ]}
              />
            )}
          </Stack>
        }
      />
      <SecretDisplayModal
        open={Boolean(hmacSecret)}
        onClose={() => setHmacSecret(null)}
        hmacSecret={hmacSecret ?? ''}
      />
    </MrAppMain>
  );
}
